/** @format */

import React, { useEffect, useRef, useState } from 'react';

import { Row, Col, Button } from 'antd';
import { CloudUploadOutlined, FileOutlined, FilePdfOutlined, InboxOutlined } from '@ant-design/icons';

import Images from '../../../Commons/Images';

import './style.css';

const ACCEPT_ATTR = 'image/*,.pdf,.doc,.docx';
const MAX_SIZE_MB = 20;

const getFileNameFromUrl = (value) => {
	if (!value || typeof value !== 'string') return 'receta';
	const cleanValue = value.split('?')[0];
	const pieces = cleanValue.split('/');
	return pieces[pieces.length - 1] || 'receta';
};

const getPreviewData = (value) => {
	if (!value) return null;

	if (typeof value === 'string') {
		const lowerValue = value.toLowerCase();
		return {
			type: lowerValue.endsWith('.pdf') ? 'application/pdf' : lowerValue.endsWith('.doc') || lowerValue.endsWith('.docx') ? 'document' : 'image',
			src: value,
			name: getFileNameFromUrl(value),
		};
	}

	const file = value.originFileObj || value;
	if (!file) return null;

	return {
		type: file.type === 'application/pdf' ? 'application/pdf' : file.type?.startsWith('image/') ? 'image' : 'document',
		src: URL.createObjectURL(file),
		name: file.name || 'receta',
	};
};

export default function UploadImage(props) {
	const { isReadOnly } = props;
	const hiddenInputRef = useRef(null);
	const objectUrlRef = useRef(null);
	const [previewData, setPreviewData] = useState(null);

	const clearObjectUrl = () => {
		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}
	};

	const setPreviewFromValue = (value) => {
		clearObjectUrl();

		if (!value) {
			setPreviewData(null);
			return;
		}

		if (typeof value === 'string') {
			setPreviewData(getPreviewData(value));
			return;
		}

		const file = value.originFileObj || value;
		if (!file) {
			setPreviewData(null);
			return;
		}

		const preview = getPreviewData(file);
		objectUrlRef.current = preview.src;
		setPreviewData(preview);
	};

	useEffect(() => {
		setPreviewFromValue(props.defaultFile);
		return () => clearObjectUrl();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.defaultFile]);

	const validateFile = (file) => {
		const isImageFile = file.type.startsWith('image/');
		const isPdfFile = file.type === 'application/pdf';
		const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
		const isDocFile = ['.doc', '.docx'].includes(fileExtension);

		if (!isImageFile && !isPdfFile && !isDocFile) {
			props.setInvalidRecipe?.(true);
			return false;
		}

		if (file.size / 1024 / 1024 >= MAX_SIZE_MB) {
			props.setInvalidRecipe?.(true);
			return false;
		}

		props.setInvalidRecipe?.(false);
		return true;
	};

	const handleFileChange = (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		if (!validateFile(file)) {
			event.target.value = '';
			return;
		}

		const nextPreview = getPreviewData(file);
		if (nextPreview?.type !== 'document') {
			clearObjectUrl();
			objectUrlRef.current = nextPreview?.src || null;
			setPreviewData(nextPreview);
		} else {
			setPreviewData(nextPreview);
		}

		props.addProductImage(file);
		event.target.value = '';
	};

	const handleOpenPicker = () => {
		if (isReadOnly) return;
		hiddenInputRef.current?.click();
	};

	return (
		<div className={`ph-recipe-upload-image-container ${isReadOnly ? 'ph-recipe-upload-image-quotation-context' : ''}`}>
			<div
				className="ph-recipe-upload-clickable-container"
				onClick={handleOpenPicker}
				role="button"
				tabIndex={0}
				onKeyDown={(event) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						handleOpenPicker();
					}
				}}
			>
				<input ref={hiddenInputRef} type="file" accept={ACCEPT_ATTR} style={{ display: 'none' }} onChange={handleFileChange} />

				{previewData ? (
					<div className="ph-recipe-upload-preview-main-container">
						<div className="ph-recipe-upload-preview-content">
							<div className={`preview-hover-container ${isReadOnly ? 'quotation-context-preview' : ''}`}>
								{previewData.type === 'application/pdf' ? (
									<div className="ph-recipe-upload-preview-pdf-container">
										<FilePdfOutlined className="ph-recipe-upload-preview-pdf-icon" />
									</div>
								) : previewData.type === 'document' ? (
									<div className="ph-recipe-upload-preview-pdf-container">
										<FileOutlined className="ph-recipe-upload-preview-pdf-icon" />
									</div>
								) : (
									<img className="ph-recipe-upload-preview-image" src={previewData.src} alt={previewData.name} />
								)}
								{!isReadOnly && (
									<div className="ph-recipe-upload-preview-header ph-recipe-upload-preview-overlay">
										<CloudUploadOutlined className="ph-recipe-upload-component-title-icon" />
										<h4 className="ph-recipe-upload-component-title">
											Haz click para cambiar tu <span className="ph-recipe-upload-component-subtitle">{props.title}</span>
										</h4>
									</div>
								)}
								{isReadOnly && (
									<div className="ph-recipe-upload-preview-header ph-recipe-upload-preview-overlay">
										<h4 className="ph-recipe-upload-component-title">Receta cotizada, no editable</h4>
									</div>
								)}
							</div>
						</div>
						<div className="file-list" style={{ marginTop: 12, width: '100%' }}>
							<Row
								align="middle"
								justify="space-between"
								className="file-row"
								style={{ padding: '8px', border: '1px solid #eee', borderRadius: 8 }}
							>
								<Col span={16}>
									<div className="file-link" style={{ padding: 0 }}>
										{previewData.type === 'application/pdf' ? <FilePdfOutlined style={{ marginRight: 6 }} /> : null}
										<span className="file-name">{previewData.name}</span>
									</div>
								</Col>
								<Col span={8} style={{ textAlign: 'right' }}>
									<Button
										type="text"
										danger
										disabled={isReadOnly}
										onClick={(e) => {
											e.stopPropagation();
											props.removeProductImage();
										}}
									>
										Eliminar
									</Button>
								</Col>
							</Row>
						</div>
					</div>
				) : (
					<div className="ph-recipe-upload-component-main-container dragger-styling">
						<p className="ant-upload-drag-icon">
							<InboxOutlined />
						</p>
						<p className="ant-upload-text">Haz clic o arrastra el archivo aquí</p>
						<p className="ph-upload-help">Formato: JPG, JPEG, PNG, PDF (Máx. 20 MB)</p>
					</div>
				)}
			</div>
		</div>
	);
}
